import { environment } from "../../environments";
import Cookies from "./cookies";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

export type PendingPurchase = {
  transaction_id: string;
  value: number;
  currency: string;
  plan: string;
};

export const GTM_PENDING_PURCHASE_KEY = "gtm_pending_purchase";

const PLAN_LABELS: Record<string, string> = {
  monthly: "mensal",
  quarterly: "trimestral",
  yearly: "anual",
};

export function pushDataLayer(payload: Record<string, unknown>): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * Fires `sign_up` from the OAuth pending cookie after a completed login.
 * Leaves the cookie in place when the visitor is not authenticated so an
 * abandoned OAuth attempt cannot record a conversion.
 */
export function trackPendingOAuthSignup(isLoggedIn: boolean): void {
  const method = Cookies.get("gtm_pending_signup");
  if (!method || !isLoggedIn) {
    return;
  }
  Cookies.remove("gtm_pending_signup");
  if (environment.BASE_COOKIE) {
    Cookies.remove("gtm_pending_signup", { domain: environment.BASE_COOKIE });
  }
  pushDataLayer({
    event: "sign_up",
    method,
  });
}

export function storePendingPurchase(input: {
  plan: string;
  recurrence: string;
  value: number;
}): void {
  const pending: PendingPurchase = {
    transaction_id: createCheckoutTransactionId(input.plan, input.recurrence),
    value: input.value,
    currency: "USD",
    plan: PLAN_LABELS[input.recurrence] || input.recurrence,
  };
  sessionStorage.setItem(GTM_PENDING_PURCHASE_KEY, JSON.stringify(pending));
}

function createCheckoutTransactionId(plan: string, recurrence: string): string {
  const nonce =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}`;
  return `checkout:${plan}:${recurrence}:${nonce}`;
}

function paymentTransactionIdFromQuery(
  query: Record<string, string | undefined>
): string | undefined {
  return (
    query["transaction_id"] ||
    query["session_id"] ||
    query["payment_id"] ||
    query["checkout_id"]
  );
}

export function trackPendingPurchase(
  query: Record<string, string | undefined> = {}
): void {
  const raw = sessionStorage.getItem(GTM_PENDING_PURCHASE_KEY);
  if (!raw) {
    return;
  }
  sessionStorage.removeItem(GTM_PENDING_PURCHASE_KEY);
  try {
    const pending = JSON.parse(raw) as PendingPurchase;
    if (typeof pending.value !== "number" || !Number.isFinite(pending.value)) {
      return;
    }
    pushDataLayer({
      event: "purchase",
      transaction_id: paymentTransactionIdFromQuery(query) || pending.transaction_id,
      value: pending.value,
      currency: pending.currency || "USD",
      plan: pending.plan,
    });
  } catch (err) {
    console.error("Failed to track purchase conversion", err);
  }
}
